import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import { getTiposModulo } from "@/services/aux-entities.service";
import {
  getLogisticDays,
  GetLogisticDaysProps,
  patchLogisticDays,
  PatchLogisticDaysProps,
} from "@/services/logistic.service";
import { getTrucksList } from "@/services/trucks.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import dayjs from "dayjs";
import { Dispatch, RefObject, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { SelectedDays } from "./logistic.schema";

export interface SelectOption {
  label: string;
  value: string;
}

interface BuildYearOptionsHandlerProps {
  setYearOptions: Dispatch<SetStateAction<SelectOption[]>>;
  setDefaultYearValue?: (value: string[]) => void;
  yearsAhead?: number;
}

export const buildYearOptionsHandler = ({
  setYearOptions,
  setDefaultYearValue,
  yearsAhead = 2,
}: BuildYearOptionsHandlerProps) => {
  const currentYear = new Date().getFullYear();
  const options: SelectOption[] = Array.from({ length: yearsAhead + 1 }).map(
    (_, idx) => {
      const year = currentYear + idx;
      return {
        label: String(year),
        value: String(year),
      };
    },
  );

  setYearOptions(options);
  setDefaultYearValue?.([String(currentYear)]);
};

interface FetchActiveTrucksOptionsHandlerProps {
  setLoadingTrucks: Dispatch<SetStateAction<boolean>>;
  setTruckOptions: Dispatch<SetStateAction<SelectOption[]>>;
  tipoModulo?: number[];
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchActiveTrucksOptionsHandler = async ({
  setLoadingTrucks,
  setTruckOptions,
  tipoModulo,
  navigate,
  fetchPermissions,
}: FetchActiveTrucksOptionsHandlerProps) => {
  try {
    setLoadingTrucks(true);

    const response = await getTrucksList({
      activo: true,
      ...(tipoModulo?.length ? { tipoModulo } : {}),
      "P.Pagina": 1,
      "P.Records": 9999,
    });

    const items = response?.listado ?? [];
    setTruckOptions(
      items.map((t: any) => ({
        label: `${t.patente ?? ""} - ${t.nombre ?? ""}`.trim(),
        value: String(t.id),
      })),
    );
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LOGISTIC,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudieron cargar los equipos activos.",
      });
    }
    setTruckOptions([]);
  } finally {
    setLoadingTrucks(false);
  }
};

interface PatchLogisticDaysHandlerProps {
  props: PatchLogisticDaysProps;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const patchLogisticDaysHandler = async ({
  props,
  navigate,
  fetchPermissions,
}: PatchLogisticDaysHandlerProps) => {
  try {
    await patchLogisticDays(props);
    toaster.create({
      type: "success",
      title: "Días actualizados exitosamente.",
    });
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron actualizar los días de logística.",
    );
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LOGISTIC,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  }
};

interface FetchModuleTypesOptionsHandlerProps {
  setLoadingModuleTypes: Dispatch<SetStateAction<boolean>>;
  setModuleTypeOptions: Dispatch<SetStateAction<SelectOption[]>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchModuleTypesOptionsHandler = async ({
  setLoadingModuleTypes,
  setModuleTypeOptions,
  navigate,
  fetchPermissions,
}: FetchModuleTypesOptionsHandlerProps) => {
  try {
    setLoadingModuleTypes(true);
    const response = await getTiposModulo();

    setModuleTypeOptions(
      (response ?? []).map((m) => ({
        label: m.descripcion,
        value: String(m.id),
      })),
    );
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron obtener los tipos de módulo",
    );
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LOGISTIC,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    setModuleTypeOptions([]);
  } finally {
    setLoadingModuleTypes(false);
  }
};

interface FetchLogisticDaysHandlerProps {
  props: GetLogisticDaysProps;
  setLoadingDays: Dispatch<SetStateAction<boolean>>;
  setDays: Dispatch<SetStateAction<any[]>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
}

export const fetchLogisticDaysHandler = async ({
  props,
  setLoadingDays,
  setDays,
  navigate,
  fetchPermissions,
  showLoad = true,
}: FetchLogisticDaysHandlerProps) => {
  try {
    if (showLoad) setLoadingDays(true);

    const response = await getLogisticDays(props);
    setDays(response ?? []);
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron obtener los días de logística.",
    );
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LOGISTIC,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    setDays([]);
  } finally {
    setLoadingDays(false);
  }
};

interface SetBlockedDaysHandlerProps {
  selectedValues: SelectedDays;
  loadingDays: boolean;
  initialBlockedDatesRef: RefObject<Set<string>>;
  initialReasonsRef: RefObject<Map<string, string>>;
  days: any[];
  setCurrentBlockedDates: Dispatch<SetStateAction<Set<string>>>;
}

export const setBlockedDaysHandler = ({
  selectedValues,
  loadingDays,
  initialBlockedDatesRef,
  initialReasonsRef,
  days,
  setCurrentBlockedDates,
}: SetBlockedDaysHandlerProps) => {
  if (!selectedValues.hasAll) return;
  if (loadingDays) return;

  const set = new Set<string>();
  const reasons = new Map<string, string>();
  (days ?? []).forEach((d: any) => {
    const raw = d?.fecha;
    if (!raw) return;
    const key = dayjs(raw).format("YYYY-MM-DD");
    if (key === "Invalid Date") return;
    set.add(key);
    if (d?.motivoBloqueo) reasons.set(key, String(d.motivoBloqueo));
  });

  initialBlockedDatesRef.current = set;
  initialReasonsRef.current = reasons;
  setCurrentBlockedDates(new Set(set));
};

interface PrepareDaysForFetchProps {
  selectedValues: SelectedDays;
  lastFetchKeyRef: RefObject<string | null>;
  setDays: Dispatch<SetStateAction<any[]>>;
  initialBlockedDatesRef: RefObject<Set<string>>;
  initialReasonsRef: RefObject<Map<string, string>>;
  setCurrentBlockedDates: Dispatch<SetStateAction<Set<string>>>;
  tiposKey: string;
}

export const prepareDaysForFetch = ({
  selectedValues,
  lastFetchKeyRef,
  setDays,
  initialBlockedDatesRef,
  initialReasonsRef,
  setCurrentBlockedDates,
  tiposKey,
}: PrepareDaysForFetchProps) => {
  if (!selectedValues.hasAll) {
    lastFetchKeyRef.current = null;
    setDays([]);
    initialBlockedDatesRef.current = new Set();
    initialReasonsRef.current = new Map();
    setCurrentBlockedDates(new Set());
    return false;
  }

  const key = `${selectedValues.year}-${selectedValues.idEquipo}-${tiposKey}`;
  if (lastFetchKeyRef.current === key) return false;
  lastFetchKeyRef.current = key;
  return true;
};
